import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';
import SEOHelmet from './SEOHelmet';
import BeforeAfterSlider from './BeforeAfterSlider';
import ProjectGallery from './ProjectGallery';
import ProjectPhoto from './ProjectPhoto';
import { SITE_CONFIG } from '../config';
import { services, projects, projectPhotos, processSteps, testimonials, generalFaqs } from '../data/renovationData';
import { useConsultationModal } from '../contexts/ConsultationModalContext';
import './LandingPage.css';

const FAQSchema = ({ faqs }) => ({
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: faqs.map(([question, answer]) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } }))
});

export default function LandingPage() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const { openConsultationModal } = useConsultationModal();
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyMotionPreference = () => {
      if (motionPreference.matches) video.pause();
      else video.play().catch(() => {});
    };
    applyMotionPreference();
    motionPreference.addEventListener('change', applyMotionPreference);
    return () => motionPreference.removeEventListener('change', applyMotionPreference);
  }, []);

  const toggleVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  return (
    <MarketingLayout branded>
      <SEOHelmet
        title="Maricopa Renovations | Kitchen, Bathroom & Home Remodeling"
        description="Local kitchen, bathroom, flooring and whole-home remodeling in Maricopa, AZ. Visit our showroom or schedule a free in-home consultation."
        schema={[FAQSchema({ faqs: generalFaqs.slice(0, 4) })]}
      />
      <section className="home-hero">
        <div className="hero-content page-width">
          <div className="hero-panel">
            <p className="eyebrow eyebrow-light">Whole-home remodeling · Flooring & more</p>
            <h1><span className="hero-title-line">Your home town</span><em className="hero-title-line">remodelers.</em></h1>
            <p>Make room for a home you love. Gary, Sandy and the Maricopa Renovations team help bring your kitchen, bathroom, flooring and whole-home projects to life.</p>
            <div className="hero-actions">
              <button className="button" type="button" onClick={() => openConsultationModal()}>Get a free consultation</button>
              <Link className="button button-light" to="/projects">See our work</Link>
            </div>
            <div className="hero-proof" aria-label="Local business details"><span><strong>Maricopa</strong>Local showroom</span><span><strong>30+ years</strong>Combined experience</span><span><strong>ROC #294216</strong>Arizona contractor</span></div>
          </div>
          <div className="hero-media">
            <div className="hero-video-frame">
              <video ref={videoRef} id="hero-renovation-video" className="hero-video" loop muted playsInline preload="metadata" poster={SITE_CONFIG.ASSETS.HERO_POSTER} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} aria-label="Animated Maricopa Renovations team installing kitchen cabinets">
                <source src={SITE_CONFIG.ASSETS.HERO_VIDEO} type="video/mp4" />
              </video>
            </div>
            <div className="hero-media-footer"><p>From first idea to finished detail.</p><button className="hero-playback" type="button" onClick={toggleVideo} aria-controls="hero-renovation-video"><span aria-hidden="true">{isPlaying ? 'Ⅱ' : '▶'}</span>{isPlaying ? 'Pause video' : 'Play video'}</button></div>
          </div>
        </div>
      </section>

      <section className="trust-bar" aria-label="Business highlights">
        <div className="page-width">
          <div><strong>Local</strong><span>Maricopa showroom</span></div>
          <div><strong>ROC number</strong><span>Arizona ROC #294216</span></div>
          <div><strong>Personal</strong><span>Free consultation</span></div>
          <div><strong>Flexible</strong><span>Financing available</span></div>
        </div>
      </section>

      <section className="content-section">
        <div className="page-width">
          <div className="section-head">
            <div><p className="eyebrow">How we can help</p><h2 className="section-title">A better space starts with the right team.</h2></div>
            <p className="section-copy">We bring design conversations, product choices and hands-on renovation together—so your project feels connected from the start.</p>
          </div>
          <div className="card-grid">
            {services.map(service => (
              <Link className="service-card" to={`/services/${service.slug}`} key={service.slug}>
                <ProjectPhoto src={service.image} alt="" loading="lazy" />
                <span className="service-card-content"><h3>{service.shortTitle}</h3><span>Explore service →</span></span>
              </Link>
            ))}
            <Link className="service-card" to="/financing">
              <img src="/images/projects/showroom-backdrop.jpg" alt="" loading="lazy" width="700" height="700" />
              <span className="service-card-content"><h3>Financing options</h3><span>Explore your options →</span></span>
            </Link>
          </div>
        </div>
      </section>

      <section className="content-section dark-section">
        <div className="page-width">
          <p className="eyebrow eyebrow-light">Our process</p>
          <h2 className="section-title">Clear steps. Fewer surprises. A result that feels right.</h2>
          <div className="process-grid">
            {processSteps.map(([number, title, copy]) => <div className="process-step" key={number}><span className="process-number">{number}</span><h3>{title}</h3><p>{copy}</p></div>)}
          </div>
        </div>
      </section>

      <section className="content-section featured-work">
        <div className="page-width">
          <div className="section-head"><div><p className="eyebrow">Before & after</p><h2 className="section-title">Real changes you can see.</h2></div><Link className="text-link" to="/projects">View all projects</Link></div>
          <div className="project-grid">
            {projects.slice(0, 2).map(project => (
              <article className="project-card" key={project.slug}>
                <BeforeAfterSlider before={project.before} after={project.after} beforeAlt={`Before the ${project.category.toLowerCase()} project`} afterAlt={`After the ${project.category.toLowerCase()} project`} label={project.title} />
                <div className="project-copy"><p className="eyebrow">{project.category}</p><h3>{project.title}</h3><p>{project.summary}</p><Link className="text-link" to={`/projects/${project.slug}`}>See the transformation</Link></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="content-section project-photo-section">
        <div className="page-width">
          <div className="section-head"><div><p className="eyebrow">From our project gallery</p><h2 className="section-title">Spaces made for everyday living.</h2></div><Link className="text-link" to="/projects#photo-gallery">Explore the photo gallery</Link></div>
          <ProjectGallery photos={projectPhotos.slice(0, 3)} />
        </div>
      </section>

      <section className="content-section story-section">
        <div className="page-width split">
          <div className="media-frame story-media"><img src={SITE_CONFIG.ASSETS.TEAM} alt="Gary and Sandy Hopkins of Maricopa Renovations" loading="lazy" width="1366" height="1366" /></div>
          <div><p className="eyebrow">Rooted in Maricopa</p><h2 className="section-title">Your neighbors. Your renovation team.</h2><p className="section-copy">Gary and Sandy have called Maricopa home for more than 19 years. Together, they have built Maricopa Renovations around a simple idea: local homeowners deserve straightforward advice, dependable craftsmanship and people who stay accountable to the finished result.</p><p className="section-copy">With more than 30 years of combined industry experience and hundreds of local homes served, the team understands both the possibilities and practical details of renovating here.</p><Link className="button button-outline" to="/about-us">Meet the team</Link></div>
        </div>
      </section>

      <section className="showroom-section" style={{backgroundImage:"linear-gradient(120deg, rgba(23,23,23,.98), rgba(23,23,23,.76)), url('/images/projects/showroom-backdrop.jpg')"}}>
        <div className="page-width split">
          <div><p className="eyebrow eyebrow-light">See it. Touch it. Picture it at home.</p><h2 className="section-title">Start at our Maricopa showroom—or let us bring the conversation to you.</h2><p>Compare materials, talk through your ideas and get help turning a long wish list into a sensible plan.</p><div className="hero-actions"><button className="button" type="button" onClick={() => openConsultationModal('Other renovation')}>Schedule a visit</button><Link className="button button-light" to="/financing">Explore financing</Link></div></div>
          <address><strong>Maricopa Renovations</strong><br />41600 W Smith Enke Rd<br />Building 2, Suite 110<br />Maricopa, AZ 85138<br /><br />Mon–Fri, 9:00–4:30<br />Weekends by appointment</address>
        </div>
      </section>

      <section className="content-section">
        <div className="page-width">
          <div className="section-head"><div><p className="eyebrow">Homeowner feedback</p><h2 className="section-title">The details people remember.</h2></div><Link className="text-link" to="/reviews">Read more reviews</Link></div>
          <div className="testimonial-grid">{testimonials.map(([name, quote]) => <figure className="quote-card" key={name}><blockquote>“{quote}”</blockquote><figcaption>{name} · Homeowner</figcaption></figure>)}</div>
        </div>
      </section>

      <section className="content-section faq-home">
        <div className="page-width split faq-split">
          <div><p className="eyebrow">Good questions are a good start</p><h2 className="section-title">Planning a renovation?</h2><p className="section-copy">Here are quick answers to a few things Maricopa homeowners ask us most.</p><Link className="text-link" to="/faq">View all questions</Link></div>
          <div className="faq-list">{generalFaqs.slice(0,4).map(([question, answer]) => <div className="faq-item" key={question}><details><summary>{question}</summary><p>{answer}</p></details></div>)}</div>
        </div>
      </section>

      <section className="cta-band"><div className="page-width"><h2>Ready to make home feel like yours again?</h2><button className="button button-light" type="button" onClick={() => openConsultationModal()}>Start with a free consultation</button></div></section>
    </MarketingLayout>
  );
}
